/**
 * ReturnShipmentCard — Reverse-logistics control surface for a single return.
 *
 * Replaces the legacy "Tracking retur" input pair with a full GLS-integrated
 * card: courier picker, "Create return AWB" CTA, AWB display + tracking link,
 * status pill, action toolbar (Print PDF, Sync, Cancel), and an inline
 * vertical timeline rendered from the unified `events` list.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck, Plus, RefreshCw, Printer, X, ExternalLink, Copy,
  Clock, AlertTriangle, Package, MapPin,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, API_BASE, formatApiError, getToken } from "@/lib/api";
import { fmtDateTz } from "@/lib/format";
import { STATUS_PILL } from "./shipmentStatusMap";

const FINAL_STATUSES = new Set(["delivered", "verified", "completed", "cancelled", "lost", "damaged"]);

const GLS_TRACK_BASE = "https://gls-group.eu/RO/ro/urmarire-colet?match=";

export default function ReturnShipmentCard({ returnId, onAfterChange }) {
  const [data, setData] = useState({ shipment: null, events: [] });
  const [statusLabels, setStatusLabels] = useState({});
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // create-form state
  const [courier, setCourier] = useState("gls");
  const [weight, setWeight] = useState(1.0);
  const [parcelCount, setParcelCount] = useState(1);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [manualAwb, setManualAwb] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/returns/couriers"),
        api.get(`/returns/${returnId}/shipment`),
      ]);
      setProviders(c.data.providers || []);
      setStatusLabels(c.data.status_labels || {});
      setData(s.data || { shipment: null, events: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [returnId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onCreate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/returns/${returnId}/shipment`, {
        courier,
        weight_kg: Number(weight),
        parcel_count: Number(parcelCount),
        notify_customer: notifyCustomer,
      });
      const awb = r.data?.shipment?.awb;
      toast.success(`AWB generat: ${awb}`);
      await refresh();
      onAfterChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onAttachManual = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/returns/${returnId}/shipment/manual`, {
        awb: manualAwb.trim(),
        courier,
      });
      toast.success(`AWB ${r.data?.shipment?.awb} atașat returului`);
      setManualAwb("");
      await refresh();
      onAfterChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onSync = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/returns/${returnId}/shipment/sync`);
      if (r.data?.pending_tracking) toast.info(r.data.message);
      else if (r.data?.ok) toast.success(`Sincronizat (${r.data.history_len || 0} evenimente)`);
      else toast.error(r.data?.error || "Sync nereușit");
      await refresh();
      onAfterChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async () => {
    setBusy(true);
    try {
      await api.delete(`/returns/${returnId}/shipment`);
      toast.success("Chemarea retur a fost anulată");
      await refresh();
      onAfterChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const printLabel = () => {
    const url = `${API_BASE}/returns/${returnId}/shipment/label`;
    const token = getToken();
    // Open in new window via fetch+blob so the Authorization header travels
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
      })
      .catch((e) => toast.error(`Eroare la printare: ${e.message}`));
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("AWB copiat");
  };

  const shipment = data.shipment;
  const isActive = shipment && !FINAL_STATUSES.has(shipment.status) && shipment.status !== "cancelled";
  // After a cancellation we go back to the create form so a new AWB can be issued.
  const showCreateForm = !shipment || shipment.status === "cancelled";

  return (
    <Card className="p-6 bg-white border-border" data-testid="return-shipment-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Truck className="h-4.5 w-4.5 text-primary" />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight">Reverse Logistics</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              Chemare retur GLS
            </div>
          </div>
        </div>
        {shipment && (
          <Button onClick={onSync} disabled={busy} variant="ghost" size="sm" data-testid="return-shipment-sync">
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 bg-secondary/60 rounded animate-pulse" />
          ))}
        </div>
      ) : showCreateForm ? (
        <CreateForm
          providers={providers}
          courier={courier} setCourier={setCourier}
          weight={weight} setWeight={setWeight}
          parcelCount={parcelCount} setParcelCount={setParcelCount}
          notifyCustomer={notifyCustomer} setNotifyCustomer={setNotifyCustomer}
          onCreate={onCreate}
          busy={busy}
          previousCancelled={shipment?.status === "cancelled"}
          manualAwb={manualAwb} setManualAwb={setManualAwb}
          onAttachManual={onAttachManual}
        />
      ) : (
        <ShipmentView
          shipment={shipment}
          events={data.events}
          statusLabels={statusLabels}
          isActive={isActive}
          busy={busy}
          onSync={onSync}
          onCancel={onCancel}
          onPrintLabel={printLabel}
          onCopy={copy}
        />
      )}
    </Card>
  );
}

// ---------- create form ----------
function CreateForm({
  providers, courier, setCourier, weight, setWeight,
  parcelCount, setParcelCount, notifyCustomer, setNotifyCustomer, onCreate, busy,
  previousCancelled, manualAwb, setManualAwb, onAttachManual,
}) {
  return (
    <div className="space-y-4" data-testid="return-shipment-create-form">
      {previousCancelled && (
        <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-xs text-rose-800 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Chemarea anterioară a fost anulată. Poți crea o nouă chemare retur.
        </div>
      )}
      <div className="bg-secondary/30 border border-border rounded-lg p-3.5 text-xs text-muted-foreground flex gap-2">
        <Package className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <div>
          Generează automat AWB retur prin curier. Adresa de ridicare este preluată din comanda Shopify a clientului,
          iar destinația este depozitul setat la <span className="font-mono">/setari/livrari</span>.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Curier</Label>
          <Select value={courier} onValueChange={setCourier}>
            <SelectTrigger className="h-9" data-testid="return-shipment-courier">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p.code} value={p.code} disabled={!p.enabled}>
                  {p.name} {!p.enabled && <span className="ml-1 text-[10px] text-muted-foreground">(Curând)</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Nr. colete</Label>
          <Input
            type="number"
            min={1}
            max={20}
            value={parcelCount}
            onChange={(e) => setParcelCount(e.target.value)}
            className="h-9"
            data-testid="return-shipment-parcel-count"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Greutate (kg)</Label>
          <Input
            type="number"
            step="0.1"
            min={0.1}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="h-9"
            data-testid="return-shipment-weight"
          />
        </div>
        <label className="col-span-2 flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={notifyCustomer}
            onChange={(e) => setNotifyCustomer(e.target.checked)}
            data-testid="return-shipment-notify"
            className="accent-primary"
          />
          Trimite email automat clientului cu instrucțiuni de pregătire colet
        </label>
      </div>

      <Button
        onClick={onCreate}
        disabled={busy}
        className="w-full"
        data-testid="return-shipment-create-btn"
      >
        <Plus className="h-4 w-4 mr-2" />
        {busy ? "Se generează..." : "Creează chemare retur"}
      </Button>

      {/* Manual AWB attach — for pickups already created directly in MyGLS */}
      <div className="pt-4 border-t border-border space-y-2">
        <div className="text-xs font-semibold text-foreground">
          Ai deja un AWB creat direct în MyGLS?
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Ex: 6245122308"
            value={manualAwb}
            onChange={(e) => setManualAwb(e.target.value)}
            className="h-9 font-mono"
            data-testid="return-shipment-manual-awb-input"
          />
          <Button
            variant="outline"
            className="h-9 shrink-0"
            disabled={busy || !manualAwb.trim()}
            onClick={onAttachManual}
            data-testid="return-shipment-manual-awb-btn"
          >
            Atașează AWB
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          AWB-ul va fi arondat acestui retur și urmărit automat (statusul se actualizează la ~10 minute).
        </p>
      </div>
    </div>
  );
}

// ---------- shipment view ----------
function ShipmentView({
  shipment, events, statusLabels, isActive, busy,
  onSync, onCancel, onPrintLabel, onCopy,
}) {
  const pill = STATUS_PILL[shipment.status] || STATUS_PILL.draft;
  const Icon = pill.icon;

  // Merge user events + courier history events
  const timeline = useMemo(() => {
    return [...(events || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [events]);

  return (
    <div className="space-y-5" data-testid="return-shipment-active">
      {/* AWB + status pill */}
      <div className="bg-gradient-to-br from-primary/5 to-yellow-50 border border-primary/15 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-primary font-semibold">
              AWB · {(shipment.courier || "GLS").toUpperCase()}{shipment.manual ? " · atașat manual" : ""}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="text-xl font-bold font-mono tracking-tight text-foreground" data-testid="return-awb-value">
                {shipment.awb}
              </div>
              <button
                onClick={() => onCopy(shipment.awb)}
                className="h-6 w-6 rounded-md hover:bg-primary/10 flex items-center justify-center transition-colors"
                title="Copiază AWB"
                data-testid="return-awb-copy"
              >
                <Copy className="h-3.5 w-3.5 text-primary" />
              </button>
            </div>
          </div>
          <Badge variant="outline" className={`gap-1.5 ${pill.color}`} data-testid="return-shipment-status">
            <Icon className="h-3 w-3" />
            {statusLabels[shipment.status] || shipment.status}
          </Badge>
        </div>
        {shipment.status_description && (
          <div className="text-xs text-muted-foreground mt-2">{shipment.status_description}</div>
        )}
        {shipment.last_status_at && (
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Ultim eveniment: {fmtDateTz(shipment.last_status_at)}
          </div>
        )}
      </div>

      {/* Quick addresses */}
      {(shipment.pickup_address?.city || shipment.delivery_address?.city) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <AddrTile title="Pickup (client)" addr={shipment.pickup_address} icon="from" />
          <AddrTile title="Destinație (depozit)" addr={shipment.delivery_address} icon="to" />
        </div>
      )}

      {/* Action toolbar */}
      <div className="flex flex-wrap gap-2">
        {!(shipment.manual && !shipment.label_b64) && (
          <Button onClick={onPrintLabel} variant="outline" size="sm" data-testid="return-shipment-print">
            <Printer className="h-4 w-4 mr-2" />
            Printează AWB
          </Button>
        )}
        <Button onClick={onSync} disabled={busy} variant="outline" size="sm" data-testid="return-shipment-sync-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} />
          Sincronizează
        </Button>
        <Button asChild variant="outline" size="sm">
          <a href={`${GLS_TRACK_BASE}${shipment.awb}`} target="_blank" rel="noreferrer" data-testid="return-shipment-gls-link">
            <ExternalLink className="h-4 w-4 mr-2" />
            Vezi pe GLS
          </a>
        </Button>
        {isActive && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 ml-auto" data-testid="return-shipment-cancel-btn">
                <X className="h-4 w-4 mr-2" />
                Anulează
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Anulează chemarea retur?</AlertDialogTitle>
                <AlertDialogDescription>
                  AWB-ul <span className="font-mono">{shipment.awb}</span> va fi marcat ca anulat în platformă.
                  Notă: anularea la curier se poate face doar înainte de pickup.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Renunță</AlertDialogCancel>
                <AlertDialogAction onClick={onCancel} className="bg-rose-600 hover:bg-rose-700">
                  Da, anulează
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Timeline */}
      <div className="pt-4 border-t border-border">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3 flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" /> Istoric procesare ({timeline.length})
        </div>
        {timeline.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-3">Niciun eveniment înregistrat încă.</div>
        ) : (
          <ol className="relative border-l-2 border-border pl-5 space-y-3" data-testid="return-shipment-timeline">
            <AnimatePresence initial={false}>
              {timeline.map((ev, i) => (
                <TimelineItem key={ev.id || i} ev={ev} statusLabels={statusLabels} />
              ))}
            </AnimatePresence>
          </ol>
        )}
      </div>
    </div>
  );
}

function TimelineItem({ ev, statusLabels }) {
  const pill = STATUS_PILL[ev.status] || STATUS_PILL.created;
  return (
    <motion.li
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.18 }}
      className="relative"
    >
      <span className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white ring-2 ring-border ${pill.dot}`} />
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium leading-snug">{ev.description}</div>
        {ev.code != null && (
          <Badge variant="outline" className="text-[10px] py-0 h-5 shrink-0">{ev.code}</Badge>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
        <span className="font-medium">{statusLabels[ev.status] || ev.status}</span>
        <span>·</span>
        <Clock className="h-3 w-3" />
        <span>{ev.at ? fmtDateTz(ev.at) : ""}</span>
        <span>·</span>
        <SourceBadge source={ev.source} actor={ev.actor} />
      </div>
    </motion.li>
  );
}

function SourceBadge({ source, actor }) {
  if (source === "user") return <span>👤 {actor || "Utilizator"}</span>;
  if (source === "courier" || source === "courier_history") return <span className="text-primary">🚚 Curier</span>;
  return <span className="text-muted-foreground">⚙ Sistem</span>;
}

function AddrTile({ title, addr, icon }) {
  return (
    <div className="bg-secondary/40 border border-border rounded-md p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        <MapPin className="h-3 w-3" />
        {title}
      </div>
      <div className="text-xs font-semibold mt-1 truncate">{addr?.contact_name || addr?.name || "—"}</div>
      <div className="text-[11px] text-muted-foreground truncate">
        {[addr?.street, addr?.house_number, addr?.city, addr?.zip_code].filter(Boolean).join(", ")}
      </div>
    </div>
  );
}
